import { useCallback, useEffect, useMemo, useState } from "react";
import AppShell from "@/components/AppShell";
import api, { formatApiError } from "@/lib/api";
import MathAuthorField from "@/components/MathAuthorField";
import MathText from "@/components/MathText";
import { Plus, Trash2, Edit3, Library, Search, X, Loader2, CheckCircle2, Sparkles, Wand2 } from "lucide-react";

const emptyForm = () => ({
  type: "mcq",
  text: "",
  options: ["", "", "", ""],
  correct_index: 0,
  sample_answer: "",
  points: 1,
  subject: "General",
  tags: "",
});

export default function QuestionBank() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [subjectFilter, setSubjectFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  // AI Generator state
  const [showGen, setShowGen] = useState(false);
  const [genForm, setGenForm] = useState({
    topic: "",
    subject: "General",
    count: 5,
    type_mix: "both",
    difficulty: "medium",
  });
  const [genBusy, setGenBusy] = useState(false);
  const [genErr, setGenErr] = useState("");
  const [genResults, setGenResults] = useState(null); // {questions, generated}
  const [genPicked, setGenPicked] = useState({});
  const [genSaving, setGenSaving] = useState(false);

  const load = useCallback(
    () => api.get("/question-bank").then((r) => setItems(r.data)),
    []
  );
  useEffect(() => {
    load();
  }, [load]);

  const subjects = useMemo(() => Array.from(new Set(items.map((x) => x.subject).filter(Boolean))), [items]);

  const filtered = items.filter((it) => {
    if (subjectFilter && it.subject !== subjectFilter) return false;
    if (typeFilter && it.type !== typeFilter) return false;
    if (q && !it.text.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  });

  const openNew = () => {
    setEditingId(null);
    setForm(emptyForm());
    setShowForm(true);
    setErr("");
  };
  const openEdit = (it) => {
    setEditingId(it.id);
    setForm({
      type: it.type,
      text: it.text,
      options: it.options || ["", "", "", ""],
      correct_index: it.correct_index ?? 0,
      sample_answer: it.sample_answer || "",
      points: it.points || 1,
      subject: it.subject || "General",
      tags: (it.tags || []).join(", "),
    });
    setShowForm(true);
    setErr("");
  };
  const closeForm = () => setShowForm(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const payload = {
        type: form.type,
        text: form.text,
        options: form.type === "mcq" ? form.options : null,
        correct_index: form.type === "mcq" ? Number(form.correct_index) : null,
        sample_answer: form.type === "descriptive" ? form.sample_answer : null,
        points: Number(form.points) || 1,
        subject: form.subject || "General",
        tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
      };
      if (editingId) {
        await api.patch(`/question-bank/${editingId}`, payload);
      } else {
        await api.post("/question-bank", payload);
      }
      setShowForm(false);
      load();
    } catch (ex) {
      setErr(formatApiError(ex));
    } finally {
      setBusy(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this question from your bank?")) return;
    await api.delete(`/question-bank/${id}`);
    load();
  };

  const openGenerator = () => {
    setGenResults(null);
    setGenPicked({});
    setGenErr("");
    setShowGen(true);
  };

  const runGenerate = async (e) => {
    e.preventDefault();
    setGenErr("");
    setGenBusy(true);
    setGenResults(null);
    try {
      const { data } = await api.post("/question-bank/generate", {
        topic: genForm.topic.trim(),
        subject: genForm.subject.trim() || "General",
        count: Number(genForm.count) || 5,
        type_mix: genForm.type_mix,
        difficulty: genForm.difficulty,
      });
      setGenResults(data);
      // Auto-select all generated by default
      const initSel = {};
      (data.questions || []).forEach((_, idx) => {
        initSel[idx] = true;
      });
      setGenPicked(initSel);
    } catch (ex) {
      setGenErr(formatApiError(ex));
    } finally {
      setGenBusy(false);
    }
  };

  const saveGenerated = async () => {
    if (!genResults) return;
    const picked = (genResults.questions || []).filter((_, i) => genPicked[i]);
    if (picked.length === 0) {
      setShowGen(false);
      return;
    }
    setGenSaving(true);
    try {
      await api.post("/question-bank/bulk", { items: picked });
      setShowGen(false);
      setGenResults(null);
      setGenPicked({});
      load();
    } catch (ex) {
      setGenErr(formatApiError(ex));
    } finally {
      setGenSaving(false);
    }
  };

  return (
    <AppShell title="Question bank" subtitle="Reusable questions you can drop into any exam.">
      {/* Filters */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search question text…"
            className="w-full bg-white border border-slate-200 rounded-xl pl-9 pr-4 py-2 focus:ring-2 focus:ring-indigo-500 outline-none"
            data-testid="bank-search"
          />
        </div>
        <select value={subjectFilter} onChange={(e) => setSubjectFilter(e.target.value)} className="bg-white border border-slate-200 rounded-xl px-3 py-2 text-sm" data-testid="bank-subject-filter">
          <option value="">All subjects</option>
          {subjects.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="bg-white border border-slate-200 rounded-xl px-3 py-2 text-sm" data-testid="bank-type-filter">
          <option value="">All types</option>
          <option value="mcq">MCQ</option>
          <option value="descriptive">Descriptive</option>
        </select>
        <button onClick={openGenerator} className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold bg-gradient-to-r from-indigo-600 to-violet-600 text-white shadow-sm hover:opacity-95 transition ml-auto" data-testid="bank-open-generator">
          <Wand2 className="w-4 h-4" /> Generate with AI
        </button>
        <button onClick={openNew} className="btn-primary text-sm" data-testid="bank-new">
          <Plus className="w-4 h-4" /> New question
        </button>
      </div>

      {/* Cards */}
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="bank-list">
        {filtered.map((it) => (
          <div key={it.id} className="qs-card p-5" data-testid={`bank-card-${it.id}`}>
            <div className="flex items-start justify-between">
              <div className="w-10 h-10 rounded-xl bg-indigo-50 text-indigo-600 grid place-items-center">
                <Library className="w-5 h-5" />
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => openEdit(it)} className="text-slate-500 hover:text-slate-800" data-testid={`bank-edit-${it.id}`}>
                  <Edit3 className="w-4 h-4" />
                </button>
                <button onClick={() => del(it.id)} className="text-rose-500 hover:text-rose-700" data-testid={`bank-delete-${it.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="mt-3 flex items-center gap-2">
              <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${it.type === "mcq" ? "bg-sky-50 text-sky-700" : "bg-amber-50 text-amber-700"}`}>
                {it.type === "mcq" ? "MCQ" : "Descriptive"}
              </span>
              <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
                {it.subject} · {it.points} pt
              </span>
            </div>
            <h3 className="mt-3 font-semibold text-slate-900 line-clamp-3"><MathText>{it.text}</MathText></h3>
            {it.type === "mcq" && (
              <ul className="mt-3 space-y-1 text-xs text-slate-600">
                {(it.options || []).map((opt, oi) => (
                  <li key={`${it.id}-opt-${oi}`} className={`flex items-center gap-1 ${it.correct_index === oi ? "text-emerald-700 font-semibold" : ""}`}>
                    <span className="w-4 h-4 grid place-items-center">{it.correct_index === oi ? <CheckCircle2 className="w-3.5 h-3.5" /> : String.fromCharCode(65 + oi) + "."}</span>
                    <MathText>{opt}</MathText>
                  </li>
                ))}
              </ul>
            )}
            {(it.tags || []).length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1">
                {(it.tags || []).map((t) => (
                  <span key={t} className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">#{t}</span>
                ))}
              </div>
            )}
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="col-span-full text-center text-slate-500 py-12" data-testid="no-bank">
            <Library className="w-8 h-8 text-slate-300 mx-auto mb-2" />
            No questions in the bank match your filters.
          </div>
        )}
      </div>

      {/* Form modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={closeForm} data-testid="bank-modal">
          <form
            onSubmit={submit}
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-auto shadow-xl"
          >
            <div className="p-6 border-b border-slate-100 flex items-center justify-between sticky top-0 bg-white">
              <h3 className="font-display text-xl font-bold text-slate-900">{editingId ? "Edit question" : "New question"}</h3>
              <button type="button" onClick={closeForm} className="text-slate-400 hover:text-slate-800" data-testid="bank-modal-close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-3">
              <div className="flex items-center gap-2">
                {["mcq", "descriptive"].map((t) => (
                  <button
                    type="button"
                    key={t}
                    onClick={() => setForm({ ...form, type: t })}
                    className={`px-4 py-2 rounded-xl border text-sm font-semibold transition ${
                      form.type === t ? "bg-indigo-600 border-indigo-600 text-white" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                    }`}
                    data-testid={`bank-type-${t}`}
                  >
                    {t === "mcq" ? "Multiple choice" : "Descriptive"}
                  </button>
                ))}
              </div>
              <div className="grid md:grid-cols-3 gap-3">
                <input
                  value={form.subject}
                  onChange={(e) => setForm({ ...form, subject: e.target.value })}
                  placeholder="Subject"
                  className="bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 focus:bg-white focus:ring-2 focus:ring-indigo-500 outline-none"
                  data-testid="bank-form-subject"
                />
                <input
                  type="number"
                  min={1}
                  value={form.points}
                  onChange={(e) => setForm({ ...form, points: e.target.value })}
                  placeholder="Points"
                  className="bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 focus:bg-white focus:ring-2 focus:ring-indigo-500 outline-none"
                  data-testid="bank-form-points"
                />
                <input
                  value={form.tags}
                  onChange={(e) => setForm({ ...form, tags: e.target.value })}
                  placeholder="Tags (comma-separated)"
                  className="bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 focus:bg-white focus:ring-2 focus:ring-indigo-500 outline-none"
                  data-testid="bank-form-tags"
                />
              </div>
              <MathAuthorField
                value={form.text}
                onChange={(v) => setForm({ ...form, text: v })}
                placeholder="Question text… use $E=mc^2$ for inline or $$…$$ for block math"
                rows={3}
                testIdBase="bank-form-text"
              />
              {form.type === "mcq" ? (
                <div className="space-y-3">
                  {form.options.map((opt, oi) => (
                    <div key={`bank-form-opt-${oi}`} className="flex items-start gap-3">
                      <label className="flex items-center gap-2 mt-2 pt-1.5">
                        <input
                          type="radio"
                          name="bank-correct"
                          checked={form.correct_index === oi}
                          onChange={() => setForm({ ...form, correct_index: oi })}
                          data-testid={`bank-form-correct-${oi}`}
                        />
                        <span className="text-xs font-bold text-slate-500 w-5">{String.fromCharCode(65 + oi)}</span>
                      </label>
                      <MathAuthorField
                        value={opt}
                        onChange={(v) => setForm({ ...form, options: form.options.map((o, k) => (k === oi ? v : o)) })}
                        placeholder={`Option ${String.fromCharCode(65 + oi)}`}
                        multiline={false}
                        compact
                        className="flex-1"
                        testIdBase={`bank-form-option-${oi}`}
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <MathAuthorField
                  value={form.sample_answer}
                  onChange={(v) => setForm({ ...form, sample_answer: v })}
                  placeholder="Sample answer (used as reference during grading)"
                  rows={4}
                  testIdBase="bank-form-sample"
                />
              )}
              {err && <p className="text-sm text-rose-600" data-testid="bank-form-error">{err}</p>}
            </div>
            <div className="p-6 border-t border-slate-100 flex justify-end gap-3 sticky bottom-0 bg-white">
              <button type="button" onClick={closeForm} className="btn-ghost text-sm">Cancel</button>
              <button type="submit" disabled={busy} className="btn-primary text-sm" data-testid="bank-form-submit">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                {editingId ? "Save changes" : "Add to bank"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* AI Generator modal */}
      {showGen && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={() => !genBusy && !genSaving && setShowGen(false)} data-testid="bank-gen-modal">
          <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] flex flex-col shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-slate-100 flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <span className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-600 to-violet-600 text-white grid place-items-center">
                    <Sparkles className="w-5 h-5" />
                  </span>
                  <h3 className="font-display text-xl font-bold text-slate-900">Generate questions with AI</h3>
                </div>
                <p className="text-xs text-slate-500 mt-1">Powered by Claude Sonnet 4.5.</p>
              </div>
              <button onClick={() => setShowGen(false)} disabled={genBusy || genSaving} className="text-slate-400 hover:text-slate-800 disabled:opacity-40" data-testid="bank-gen-close">
                <X className="w-5 h-5" />
              </button>
            </div>
            {!genResults ? (
              <form onSubmit={runGenerate} className="p-6 space-y-3">
                <div>
                  <label className="text-xs uppercase tracking-widest font-bold text-slate-500">Topic</label>
                  <input
                    required
                    value={genForm.topic}
                    onChange={(e) => setGenForm({ ...genForm, topic: e.target.value })}
                    placeholder="e.g. Photosynthesis, Fractions, Shakespeare's Hamlet…"
                    className="w-full mt-1.5 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 focus:bg-white focus:ring-2 focus:ring-indigo-500 outline-none"
                    data-testid="bank-gen-topic"
                  />
                </div>
                <div className="grid md:grid-cols-4 gap-3">
                  <div>
                    <label className="text-xs uppercase tracking-widest font-bold text-slate-500">Subject</label>
                    <input
                      value={genForm.subject}
                      onChange={(e) => setGenForm({ ...genForm, subject: e.target.value })}
                      className="w-full mt-1.5 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 focus:bg-white focus:ring-2 focus:ring-indigo-500 outline-none"
                      data-testid="bank-gen-subject"
                    />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-widest font-bold text-slate-500">Count</label>
                    <input
                      type="number"
                      min={1}
                      max={15}
                      value={genForm.count}
                      onChange={(e) => setGenForm({ ...genForm, count: e.target.value })}
                      className="w-full mt-1.5 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 focus:bg-white focus:ring-2 focus:ring-indigo-500 outline-none"
                      data-testid="bank-gen-count"
                    />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-widest font-bold text-slate-500">Mix</label>
                    <select
                      value={genForm.type_mix}
                      onChange={(e) => setGenForm({ ...genForm, type_mix: e.target.value })}
                      className="w-full mt-1.5 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 focus:bg-white focus:ring-2 focus:ring-indigo-500 outline-none"
                      data-testid="bank-gen-mix"
                    >
                      <option value="both">MCQ + Descriptive</option>
                      <option value="mcq">MCQ only</option>
                      <option value="descriptive">Descriptive only</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-widest font-bold text-slate-500">Difficulty</label>
                    <select
                      value={genForm.difficulty}
                      onChange={(e) => setGenForm({ ...genForm, difficulty: e.target.value })}
                      className="w-full mt-1.5 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 focus:bg-white focus:ring-2 focus:ring-indigo-500 outline-none"
                      data-testid="bank-gen-difficulty"
                    >
                      <option value="easy">Easy</option>
                      <option value="medium">Medium</option>
                      <option value="hard">Hard</option>
                    </select>
                  </div>
                </div>
                {genErr && <p className="text-sm text-rose-600" data-testid="bank-gen-error">{genErr}</p>}
                <div className="flex justify-end gap-3 pt-2">
                  <button type="button" onClick={() => setShowGen(false)} className="btn-ghost text-sm">Cancel</button>
                  <button type="submit" disabled={genBusy || !genForm.topic.trim()} className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold bg-gradient-to-r from-indigo-600 to-violet-600 text-white shadow-sm disabled:opacity-60" data-testid="bank-gen-run">
                    {genBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
                    {genBusy ? "Generating…" : "Generate"}
                  </button>
                </div>
              </form>
            ) : (
              <>
                <div className="p-5 border-b border-slate-100 flex items-center justify-between">
                  <p className="text-sm text-slate-700">
                    <b>{genResults.generated}</b> question{genResults.generated === 1 ? "" : "s"} ready · pick which to save.
                  </p>
                  <button onClick={() => { setGenResults(null); setGenPicked({}); }} className="text-xs font-semibold text-indigo-600 hover:underline" data-testid="bank-gen-again">
                    Try again
                  </button>
                </div>
                <div className="flex-1 overflow-auto p-5 space-y-2">
                  {(genResults.questions || []).map((q, idx) => {
                    const key = q._key || `gen-${idx}-${(q.text || "").slice(0, 20)}`;
                    const selected = !!genPicked[idx];
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => setGenPicked({ ...genPicked, [idx]: !selected })}
                        className={`w-full text-left p-4 rounded-xl border transition flex items-start gap-3 ${
                          selected ? "bg-indigo-50 border-indigo-500" : "bg-white border-slate-200 hover:border-slate-300"
                        }`}
                        data-testid={`bank-gen-item-${idx}`}
                      >
                        <span className={`mt-0.5 w-5 h-5 rounded grid place-items-center shrink-0 ${selected ? "bg-indigo-600 text-white" : "bg-white border border-slate-300"}`}>
                          {selected && <CheckCircle2 className="w-4 h-4" />}
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${q.type === "mcq" ? "bg-sky-50 text-sky-700" : "bg-amber-50 text-amber-700"}`}>
                              {q.type === "mcq" ? "MCQ" : "Descriptive"}
                            </span>
                            <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
                              {q.subject} · {q.points} pt
                            </span>
                          </div>
                          <p className="mt-2 text-sm font-semibold text-slate-800">{q.text}</p>
                          {q.type === "mcq" && (
                            <ul className="mt-2 space-y-0.5 text-xs text-slate-600">
                              {(q.options || []).map((opt, oi) => (
                                <li key={`gen-${idx}-opt-${oi}`} className={q.correct_index === oi ? "text-emerald-700 font-semibold" : ""}>
                                  {String.fromCharCode(65 + oi)}. {opt} {q.correct_index === oi && "✓"}
                                </li>
                              ))}
                            </ul>
                          )}
                          {q.type === "descriptive" && q.sample_answer && (
                            <p className="mt-2 text-xs italic text-slate-500">Sample: {q.sample_answer}</p>
                          )}
                        </div>
                      </button>
                    );
                  })}
                  {(genResults.questions || []).length === 0 && (
                    <p className="text-center text-slate-500 py-6">No questions returned — try a different topic or count.</p>
                  )}
                </div>
                {genErr && <p className="px-5 pb-3 text-sm text-rose-600">{genErr}</p>}
                <div className="p-5 border-t border-slate-100 flex items-center justify-between">
                  <p className="text-xs text-slate-500">
                    {Object.values(genPicked).filter(Boolean).length} selected
                  </p>
                  <div className="flex gap-3">
                    <button onClick={() => setShowGen(false)} className="btn-ghost text-sm">Cancel</button>
                    <button onClick={saveGenerated} disabled={genSaving} className="btn-primary text-sm" data-testid="bank-gen-save">
                      {genSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                      Save to bank
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </AppShell>
  );
}
